import React, { useState, useEffect } from "react";
import Footer from "../components/Footer"; // Adjust this path if needed
import contentService from "../services/contentService";
import contact from "../assets/contactUs.jpg"


export default function Contact({ content }) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const [touched, setTouched] = useState({
    name: false,
    email: false,
    phone: false,
    message: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [smsConsent, setSmsConsent] = useState(false);


  // Regex validators
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Phone validation: exactly 10 digits, no special characters or letters, not all same digit
  const validatePhone = (phone) => {
    const trimmed = phone.trim();
    // Must be exactly 10 digits
    if (trimmed.length !== 10) return false;
    // Must contain only digits
    if (!/^\d+$/.test(trimmed)) return false;
    // Must not be all the same digit (like 0000000000, 1111111111, etc.)
    if (new Set(trimmed).size === 1) return false;
    return true;
  };

  // Individual field validation
  const isValidName = formData.name.trim().length >= 2;
  const isValidEmail = emailRegex.test(formData.email.trim());
  const isValidMessage = formData.message.trim().length >= 10;
  const isValidPhone = validatePhone(formData.phone);

  // Update overall form validation status (including SMS consent)
  useEffect(() => {
    setIsFormValid(
      isValidName && isValidEmail && isValidMessage && isValidPhone && smsConsent
    );
  }, [isValidName, isValidEmail, isValidMessage, isValidPhone, smsConsent]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // For phone field, only allow digits
    if (name === 'phone') {
      // Remove any non-digit characters
      const digitsOnly = value.replace(/\D/g, '');
      // Limit to 10 digits
      const limitedValue = digitsOnly.slice(0, 10);
      setFormData((prev) => ({ ...prev, [name]: limitedValue }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormValid || !smsConsent) {
      setStatusMessage("Please fill in all required fields correctly and accept SMS consent.");
      return;
    }

    setIsSubmitting(true);
    setStatusMessage("Sending your message...");

    try {
      // Submit contact form to API
      const response = await contentService.submitContactForm(formData);

      // Only treat as success if the API explicitly confirms success
      if (response.success === true) {
        setStatusMessage("Thank you! Your message has been sent.");
        setFormData({
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
        });
        setTouched({
          name: false,
          email: false,
          phone: false,
          message: false,
        });
        setSmsConsent(false);
      } else {
        setStatusMessage(response.error || "Oops! Something went wrong. Please try again later.");
      }
    } catch (error) {
      setStatusMessage(error.message || "Oops! Something went wrong. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div style={{ height: 72 }} /> {/* Spacer for sticky header */}

      {/* Contact Hero */}
      <section className="py-5 bg-white">
        <div className="container">
          <div className="row align-items-center gx-5">
            <div className="col-md-7" data-aos="fade-right">
              <h1
                className="fw-bold mb-2"
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  color: "#2e3d9c",
                  fontSize: "2.1rem",
                }}
              >
                Contact{" "}
                <span
                  style={{
                    background: "linear-gradient(90deg, #6B46C1, #D53F8C)",
                    color: "transparent",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                  }}
                >
                  {content?.title?.split(' ').slice(1).join(' ') || "Nexen Consulting"}
                </span>
              </h1>
              <p
                className="mb-3 fs-5 text-secondary"
                style={{ maxWidth: 520 }}
              >
                {content?.description || "We're here to help! Reach out for a consultation, quote, or any question—our experts will respond promptly."}
              </p>
            </div>
            <div className="col-md-5 text-center" data-aos="fade-left">
              <img
                src={contact}
                alt="Contact Nexen Consulting"
                className="img-fluid rounded shadow"
                style={{ maxHeight: 170, objectFit: "cover" }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form & Details */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="row g-4">
            {/* Contact Form */}
            <div className="col-lg-7">
              <div className="card shadow-sm rounded-4 border-0 p-4">
                <h2
                  className="fw-bold mb-3"
                  style={{ color: "#6B46C1", fontFamily: "'Poppins', sans-serif" }}
                >
                  Get In Touch
                </h2>
                <form onSubmit={handleSubmit} noValidate>
                  <div className="mb-3">
                    <label htmlFor="name" className="form-label">
                      Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      className={`form-control ${touched.name && !isValidName
                          ? "is-invalid"
                          : touched.name && isValidName
                            ? "is-valid"
                            : ""
                        }`}
                      placeholder="Your Name"
                      value={formData.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={isSubmitting}
                      required
                      minLength={2}
                    />
                    {touched.name && !isValidName && (
                      <div className="invalid-feedback">
                        Please enter your full name (minimum 2 characters).
                      </div>
                    )}
                  </div>
                  <div className="mb-3">
                    <label htmlFor="email" className="form-label">
                      Email <span className="text-danger">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      className={`form-control ${touched.email && !isValidEmail
                          ? "is-invalid"
                          : touched.email && isValidEmail
                            ? "is-valid"
                            : ""
                        }`}
                      placeholder="your@email.com"
                      value={formData.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={isSubmitting}
                      required
                    />
                    {touched.email && !isValidEmail && (
                      <div className="invalid-feedback">
                        Please enter a valid email address.
                      </div>
                    )}
                  </div>
                  <div className="mb-3">
                    <label htmlFor="phone" className="form-label">
                      Mobile Number <span className="text-danger">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      className={`form-control ${touched.phone && !isValidPhone
                          ? "is-invalid"
                          : touched.phone && isValidPhone
                            ? "is-valid"
                            : ""
                        }`}
                      placeholder="Enter 10-digit mobile number"
                      value={formData.phone}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={isSubmitting}
                      required
                      maxLength={10}
                      pattern="[0-9]{10}"
                    />
                    {touched.phone && !isValidPhone && (
                      <div className="invalid-feedback">
                        {formData.phone.trim().length === 0
                          ? "Mobile number is required"
                          : formData.phone.trim().length !== 10
                            ? "Mobile number must be exactly 10 digits"
                            : !/^\d+$/.test(formData.phone.trim())
                              ? "Mobile number must contain only digits (0-9)"
                              : new Set(formData.phone.trim()).size === 1
                                ? "Please enter a valid mobile number"
                                : "Please enter a valid 10-digit mobile number"}
                      </div>
                    )}
                  </div>
                  <div className="mb-3">
                    <label htmlFor="subject" className="form-label">
                      Subject (Optional)
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      className="form-control"
                      placeholder="Subject"
                      value={formData.subject}
                      onChange={handleChange}
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="message" className="form-label">
                      Message <span className="text-danger">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows="4"
                      className={`form-control ${touched.message && !isValidMessage
                          ? "is-invalid"
                          : touched.message && isValidMessage
                            ? "is-valid"
                            : ""
                        }`}
                      placeholder="How can we help?"
                      value={formData.message}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={isSubmitting}
                      required
                      minLength={10}
                    ></textarea>
                    {touched.message && !isValidMessage && (
                      <div className="invalid-feedback">
                        Message should be at least 10 characters.
                      </div>
                    )}
                  </div>

                  {/* SMS Consent Checkbox */}
                  <div className="mb-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="smsConsent"
                        checked={smsConsent}
                        onChange={(e) => setSmsConsent(e.target.checked)}
                        disabled={isSubmitting}
                        style={{ marginTop: "0.25rem" }}
                      />
                      <label className="form-check-label" htmlFor="smsConsent" style={{ fontSize: "0.9rem", lineHeight: "1.5", color: "#2f2f31ff" }}>
                        By subscribing and submitting, you agree to receive notifications from Nexen Consults LLC through calls and SMS. Data rates apply and messaging frequency may vary. You can unsubscribe at any time. We respect your privacy and won’t share your information with third parties.<span className="text-danger">*</span>
                      </label>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary px-5 py-2 rounded-pill"
                    disabled={!isFormValid || !smsConsent || isSubmitting}
                  >
                    {isSubmitting ? "Sending..." : "Send Message"}
                  </button>
                  {statusMessage && (
                    <div
                      className={`alert mt-3 ${statusMessage.includes("sent")
                          ? "alert-success"
                          : "alert-danger"
                        }`}
                      role="alert"
                    >
                      {statusMessage}
                    </div>
                  )}
                </form>
              </div>
            </div>

            {/* Contact Info */}
            <div className="col-lg-5">
              <div className="card shadow-sm rounded-4 border-0 p-4 mb-4">
                <h3
                  className="fw-bold mb-3"
                  style={{ color: "#D53F8C", fontFamily: "'Poppins', sans-serif" }}
                >
                  Contact Details
                </h3>
                <ul className="list-unstyled mb-3 fs-6">
                  <li>
                    <i className="bi bi-geo-alt-fill text-primary me-2"></i>
                    <b>Address:</b> {content?.address || "DALLAS, GA, 30132, USA"}
                  </li>
                  <li className="mt-2">
                    <i className="bi bi-envelope-at-fill text-primary me-2"></i>
                    <b>Email:</b>{" "}
                    <a href={`mailto:${content?.email || "info@nexenconsults.com"}`} className="text-decoration-none">
                      {content?.email || "info@nexenconsults.com"}
                    </a>
                  </li>
                  <li className="mt-2">
                    <i className="bi bi-phone-fill text-primary me-2"></i>
                    <b>Phone:</b>{" "}
                    <a href={`tel:${content?.phone ? content.phone.replace(/[\s\-()]/g, '') : '+919966969809'}`} className="text-decoration-none">
                      {content?.phone || "+91 996 696 9809"}
                    </a>
                  </li>
                  <li className="mt-2">
                    <i className="bi bi-phone-fill text-primary me-2"></i>
                    <b>Emergency Phone:</b>
                    <a href="tel:+19436789878" className="text-decoration-none ms-4">
                      +1 (943) 678 9878
                    </a>
                  </li> 
                </ul>
                <div className="border-top pt-3 mt-2">
                  <b>Office Hours:</b>
                  <br />
                  <span className="text-secondary">{content?.officeHours || "Monday – Saturday: 24/7 Support, PST\nSunday: By Appointment"}</span>
                </div>
              </div>
              {/* Embedded Google Map */}
              {/* <div
                className="rounded-4 overflow-hidden shadow"
                style={{ minHeight: 220 }}
              >
                <iframe
                  title="Nexen Consulting Office"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2724.815384135153!2d-84.8041890218321!3d33.91107390413529!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88f530f5e27b2225%3A0x3184cd8bb836b280!2s1275%20Merchants%20Dr%2C%20Dallas%2C%20GA%2030132!5e1!3m2!1sen!2sus!4v1764774371590!5m2!1sen!2sus" 
                  
                  width="100%"
                  height="220"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                />
              </div> */}
            </div>
          </div>
        </div>

        {/* Bootstrap Icons CDN */}
        <link
          rel="stylesheet"
          href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.10.3/font/bootstrap-icons.css"
        />
      </section>

    </>
  );
}
